import {
  CheckCircleIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { getLoanTextMessages, inviteLoanTextMessageOptIn, sendLoanTextMessage, streamLoanTextMessages } from 'services'
import Api from 'services/api'
import { svgLoading } from 'stories/assets'
import { formatTime } from 'utils'
import { confirm } from 'utils/modals'

import { ITextMessage, ITextRecipient, ITextThread } from './types'

/** A GSM-7 SMS segment. Past this a message is billed - and can arrive - as multiple texts. */
const SEGMENT_LENGTH = 160
/**
 * Safety net only. Replies arrive by live stream; this catches the case where the stream is down
 * (an old backend without the endpoint, a proxy that will not hold the connection) so the thread
 * still updates, just not instantly.
 */
const POLL_INTERVAL = 30000

const statusLabel: Record<string, string> = {
  queued: 'Sending...',
  sent: 'Sent',
  delivered: 'Delivered',
  undelivered: 'Not delivered',
  failed: 'Failed',
  received: '',
}

const recipientLabel = (r: ITextRecipient) => `${r.name}${r.phoneDisplay ? ` - ${r.phoneDisplay}` : ''}`

/**
 * The borrower text (SMS) conversation for the loan currently in the Api loan-number header.
 * Rendered as the Text Messages tab of Servicing > Messages and inside the Text Messages floater,
 * so it owns its own loading, polling and sending rather than taking data from a parent.
 */
export const TextMessageThread = ({ compact = false }: { compact?: boolean }) => {
  const [thread, setThread] = useState<ITextThread | null>(null)
  const [isLoading, setLoading] = useState(true)
  const [isSending, setSending] = useState(false)
  const [isInviting, setInviting] = useState(false)
  const [selectedPhone, setSelectedPhone] = useState('')
  const [draft, setDraft] = useState('')
  const bottomRef = useRef<HTMLDivElement | null>(null)
  // Read inside the poll timer, which closes over its first render, so it needs a ref not state.
  const streamingRef = useRef(false)
  const setStreaming = (on: boolean) => {
    streamingRef.current = on
  }

  const load = (showSpinner = false) => {
    if (showSpinner) setLoading(true)
    return getLoanTextMessages()
      .then((res) => setThread(res))
      .catch(() => setThread(null))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load(true)

    // The stream pushes a message the instant Twilio posts the borrower's reply.
    const stopStream = streamLoanTextMessages(Api.loanNumber, {
      onActivity: () => load(),
      onOpen: () => setStreaming(true),
      onClose: () => setStreaming(false),
    })

    // Fallback poll, and a catch-up whenever a hidden tab is brought back into view. Skipped while
    // the stream is up, so the normal case costs one connection rather than a request every cycle.
    const tick = () => {
      if (!document.hidden && !streamingRef.current) load()
    }
    const timer = setInterval(tick, POLL_INTERVAL)
    const onVisible = () => {
      if (!document.hidden) load()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      stopStream()
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const recipients = thread?.recipients || []

  // Default to the primary borrower, and keep a valid selection if the loan's borrowers change.
  useEffect(() => {
    if (!recipients.length) return
    if (recipients.some((r) => r.phone === selectedPhone)) return
    setSelectedPhone(recipients[0].phone)
  }, [recipients, selectedPhone])

  const recipient = recipients.find((r) => r.phone === selectedPhone)

  const messages = useMemo(
    () => (thread?.messages || []).filter((m) => !selectedPhone || m.phone === selectedPhone),
    [thread, selectedPhone],
  )

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'nearest' })
  }, [messages.length])

  const blockedReason = useMemo(() => {
    if (isLoading || !thread) return ''
    if (!thread.enabled)
      return 'Borrower texting is turned off for this company. An admin can enable it in Admin Tools > Configuration > Rules Configuration.'
    if (!thread.configured) return 'No text-message sending number is configured for this environment.'
    if (!recipients.length)
      return 'No borrower on this loan has a phone number on file. Add one on the Borrower Information screen to text them.'
    if (recipient?.optedOut)
      return `${recipient.name} replied STOP to a previous text and has opted out. They have to text START back before this number can be messaged again.`
    if (thread.requireConsent && recipient && !recipient.consented)
      return `${recipient.name} has not opted in to text messages. Send them an invitation, or ask them to text JOIN to ${thread.fromNumber || 'our number'}.`
    return ''
  }, [isLoading, thread, recipients, recipient])

  /** Only offer the invitation for the one block an email can actually clear. */
  const needsInvite = Boolean(thread?.requireConsent && recipient && !recipient.consented && !recipient.optedOut)

  const onSend = async () => {
    const body = draft.trim()
    if (!body || !recipient) return
    setSending(true)
    try {
      await sendLoanTextMessage(recipient.phone, body)
      setDraft('')
      await load()
    } catch (error: any) {
      toast(error?.message || 'The text message could not be sent.', { type: 'error' })
    } finally {
      setSending(false)
    }
  }

  const onInvite = async (channel: 'email' | 'text') => {
    if (!recipient) return

    // A text invitation goes to someone with no consent on record. That is defensible only on the
    // servicer's prior written authorization, so the person sending it says so explicitly first.
    if (channel === 'text') {
      // Hoisted to a const rather than written inline in the call: the production Babel
      // constant-elements transform crashes on inline JSX passed straight into a function.
      const content = (
        <div className="text-left text-[14px]">
          <p className="mb-2">
            This sends a text message to <span className="font-semibold">{recipient.name}</span>, who has not opted in.
          </p>
          <p className="mb-2">
            Only do this if the borrower has already given written authorization to be contacted by text, on file for
            this loan. Texting someone who has not is a compliance violation, not just a bad look.
          </p>
          <p>Send the invitation by text?</p>
        </div>
      )
      const ok = await confirm(content)
      if (!ok) return
    }

    setInviting(true)
    try {
      const res: any = await inviteLoanTextMessageOptIn(recipient.phone, channel)
      toast(
        channel === 'text'
          ? `Invitation texted to ${res?.phone || recipient.name}.`
          : `Invitation sent to ${res?.email || recipient.name}.`,
        { type: 'success' },
      )
      await load()
    } catch (error: any) {
      toast(error?.message || 'The invitation could not be sent.', { type: 'error' })
    } finally {
      setInviting(false)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      onSend()
    }
  }

  const segments = Math.max(1, Math.ceil(draft.length / SEGMENT_LENGTH))
  const canSend = Boolean(draft.trim()) && !isSending && !blockedReason

  const renderMessage = (m: ITextMessage) => {
    const isOutbound = m.direction === 'outbound'
    const failed = m.status === 'failed' || m.status === 'undelivered'
    return (
      <div key={m.id} className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
        <div
          className={`max-w-[80%] rounded-lg px-3 py-2 border ${
            isOutbound
              ? failed
                ? 'bg-red-50 border-red-200'
                : 'bg-shade-blue/10 border-shade-blue/20'
              : 'bg-gray-50 border-gray-200'
          }`}
        >
          <div className="text-gray-800 whitespace-pre-wrap break-words text-sm">{m.body}</div>
          <div className="flex items-center gap-1 mt-1 text-[11px] text-gray-500">
            <span>{m.createdAt ? formatTime(m.createdAt) : ''}</span>
            {isOutbound && (
              <>
                <span>-</span>
                <span className={failed ? 'text-red-600 font-semibold' : ''}>{statusLabel[m.status] || m.status}</span>
                {m.status === 'delivered' && <CheckCircleIcon className="w-3.5 h-3.5 text-green-600" />}
                {m.sentByName && <span className="italic">by {m.sentByName}</span>}
              </>
            )}
          </div>
          {failed && m.errorMessage && <div className="text-[11px] text-red-600 mt-0.5">{m.errorMessage}</div>}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {recipients.length > 1 && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm text-gray-500 whitespace-nowrap">Texting:</span>
          <div className="relative">
            <select
              value={selectedPhone}
              onChange={(e) => setSelectedPhone(e.target.value)}
              className="appearance-none border border-gray-300 rounded pl-3 pr-8 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-shade-blue/40"
            >
              {recipients.map((r) => (
                <option key={r.phone} value={r.phone}>
                  {recipientLabel(r)}
                  {r.optedOut ? ' (opted out)' : ''}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="w-4 h-4 absolute right-2 top-2 text-gray-400 pointer-events-none" />
          </div>
        </div>
      )}
      {recipients.length === 1 && (
        <div className="text-sm text-gray-500 mb-3">
          Texting <span className="font-semibold text-gray-800">{recipientLabel(recipients[0])}</span>
        </div>
      )}

      <div
        className={`flex-1 space-y-3 overflow-y-auto pr-1 border border-gray-100 rounded p-3 bg-white ${
          compact ? 'min-h-[16rem]' : 'min-h-[24rem] max-h-[32rem]'
        }`}
      >
        {isLoading ? (
          <div className="flex justify-center py-8">
            <img src={svgLoading} className="w-6 h-6 animate-spin" alt="loading" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            No text messages on this loan yet. Anything you send here, and every reply, stays in this thread.
          </div>
        ) : (
          messages.map(renderMessage)
        )}
        <div ref={bottomRef} />
      </div>

      {blockedReason && (
        <div className="flex items-start gap-2 mt-3 p-3 rounded bg-yellow-50 border border-yellow-200 text-sm text-yellow-800">
          <ExclamationTriangleIcon className="w-5 h-5 flex-none" />
          <span className="flex-1">
            {blockedReason}
            {/* The invitation is offered exactly where the block is explained, so the fix is one
                click from the problem rather than somewhere the servicer has to go looking for. */}
            {needsInvite && (
              <>
                <button
                  type="button"
                  onClick={() => onInvite('email')}
                  disabled={isInviting}
                  className="ml-2 underline font-semibold hover:no-underline disabled:opacity-60"
                >
                  {isInviting ? 'Sending...' : 'Invite by email'}
                </button>
                <span className="mx-1 text-yellow-600">or</span>
                <button
                  type="button"
                  onClick={() => onInvite('text')}
                  disabled={isInviting}
                  className="underline font-semibold hover:no-underline disabled:opacity-60"
                >
                  by text
                </button>
              </>
            )}
          </span>
        </div>
      )}

      <div className="mt-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          rows={compact ? 2 : 3}
          maxLength={thread?.maxLength || 1600}
          disabled={Boolean(blockedReason)}
          placeholder={blockedReason ? 'Texting is unavailable for this borrower.' : 'Write a text message...'}
          className="w-full border border-gray-300 rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-shade-blue/40 disabled:bg-gray-50 disabled:text-gray-400"
        />
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-gray-500">
            {draft.length} characters
            {draft.length > SEGMENT_LENGTH ? ` - ${segments} messages` : ''}
            <span className="ml-2 hidden sm:inline">Cmd/Ctrl + Enter to send</span>
          </span>
          <button
            onClick={onSend}
            disabled={!canSend}
            className="flex items-center gap-2 px-4 py-2 rounded text-sm font-semibold text-white bg-shade-blue hover:opacity-90 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
            {isSending ? 'Sending...' : 'Send Text'}
          </button>
        </div>
      </div>
    </div>
  )
}
